import { buildOptimisticRejectedProfiles } from '@/modules/companyNeed/helpers/rejectOptimistic';
import { useCompanyNeedStore } from '@/modules/companyNeed/store/companyNeedStore';

import type { RejectionSuccessPayload } from '@/components/modal/Profile/rejectProfile/types';
import type { OptimisticRejectionWorker } from '@/modules/companyNeed/helpers/rejectOptimistic';
import type { CompanyNeedReloadSection } from '@/modules/companyNeed/types';

import { RejectionType } from '@/components/modal/Profile/rejectProfile/types';

const DEFAULT_MATCHING_PAGE_LIMIT = 25;

export const useCompanyNeedOptimistic = () => {
	const store = useCompanyNeedStore();

	/**
	 * After optimistic removals empty the current matching page, refetch so the
	 * next profiles (or the empty state) are shown instead of a blank table.
	 */
	const refreshMatchingIfCurrentPageEmpty = () => {
		const matching = store.visibleMatchingWorkers;
		if (matching.items.length > 0) return;

		const pageLimit =
			matching.limit > 0 ? matching.limit : DEFAULT_MATCHING_PAGE_LIMIT;
		const currentPage = Math.max(1, matching.offset || 1);
		const maxPage = Math.max(1, Math.ceil(matching.total / pageLimit) || 1);
		const targetPage = Math.min(currentPage, maxPage);

		void store.fetchMatchingWorkers({
			offset: (targetPage - 1) * pageLimit,
			limit: pageLimit,
		});
	};

	const applyRejection = (
		workers: OptimisticRejectionWorker[],
		payload: RejectionSuccessPayload
	) => {
		if (payload.rejectionType === RejectionType.FOR_NEED) {
			store.applyOptimisticForNeedRejection(
				buildOptimisticRejectedProfiles(workers, payload)
			);
		} else {
			store.applyOptimisticGlobalRejection(workers.map((worker) => worker.id));
		}
	};

	const reloadAfterRejection = (
		rejectionType: RejectionType,
		extraSections: CompanyNeedReloadSection[] = []
	) => {
		const sections: CompanyNeedReloadSection[] = [
			'companyNeed',
			...extraSections,
		];
		if (rejectionType === RejectionType.FOR_NEED)
			sections.push('rejectedProfiles');
		void store.reloadData(sections);
	};

	const handleRejectionFromMatching = (
		workers: OptimisticRejectionWorker[],
		payload: RejectionSuccessPayload
	) => {
		applyRejection(workers, payload);
		reloadAfterRejection(payload.rejectionType);
		refreshMatchingIfCurrentPageEmpty();
	};

	const handleRejectionFromCandidates = (
		workers: OptimisticRejectionWorker[],
		payload: RejectionSuccessPayload
	) => {
		applyRejection(workers, payload);
		store.applyOptimisticCandidatesRemoval(workers.map((worker) => worker.id));
		reloadAfterRejection(payload.rejectionType);
	};

	const handleRejectionFromRecruitment = (
		workers: OptimisticRejectionWorker[],
		payload: RejectionSuccessPayload
	) => {
		applyRejection(workers, payload);
		store.applyOptimisticRecruitmentRemoval(workers.map((worker) => worker.id));
		reloadAfterRejection(payload.rejectionType, ['recruitment']);
	};

	const handleMoveToPoolFromRecruitment = (workerIds: string[]) => {
		const ids = workerIds.filter(Boolean);
		if (ids.length === 0) return;
		store.applyOptimisticPoolAddition(ids.length);
	};

	const handleMoveToPoolFromMatching = (workerIds: string[]) => {
		const ids = workerIds.filter(Boolean);
		if (ids.length === 0) return;
		store.applyOptimisticGlobalRejection(ids);
		store.applyOptimisticPoolAddition(ids.length);
	};

	const handleMoveFromCandidates = (workerIds: string[]) => {
		store.applyOptimisticCandidatesRemoval(workerIds);
	};

	const reloadAfterPoolMove = (options: { fromRecruitment?: boolean } = {}) => {
		const sections: CompanyNeedReloadSection[] = [
			'companyNeed',
			'poolWorkers',
			'matchingWorkers',
		];
		if (options.fromRecruitment) sections.push('recruitment');
		void store.reloadData(sections);
	};

	const handleRemoveFromAgency = (
		workerIds: string[],
		extraSections: CompanyNeedReloadSection[] = []
	) => {
		store.applyOptimisticRemoveFromAgency(workerIds);
		void store.reloadData([
			'companyNeed',
			'matchingWorkers',
			'poolWorkers',
			...extraSections,
		]);
	};

	return {
		handleRejectionFromMatching,
		handleRejectionFromCandidates,
		handleRejectionFromRecruitment,
		handleMoveToPoolFromRecruitment,
		handleMoveToPoolFromMatching,
		handleMoveFromCandidates,
		reloadAfterPoolMove,
		handleRemoveFromAgency,
		refreshMatchingIfCurrentPageEmpty,
	};
};
